import { Box, Button, Heading, Spinner, Text, useToast, VStack } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import QRCode from 'react-qr-code';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CustomToast from 'components/CustomToast';
import FormField from 'components/FormField';
import apiService from 'services';
import buildOtpAuthProvisioningUri from 'utils/buildOtpAuthProvisioningUri';
import mapAuthApiError from 'utils/mapAuthApiError';
import { clearMfaSessionStorage, readMfaSetupContext } from 'utils/mfaSessionStorage';
import { MfaTotpCodeSchema } from 'validation/schemas/AuthenticationSchema';

import AuthenticationMfaShell from './AuthenticationMfaShell';

const initialValues = { code: '' };

const MfaSetupPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [secretCode, setSecretCode] = useState<string | null>(null);
  const [accountEmail, setAccountEmail] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingSecret, setLoadingSecret] = useState(true);

  const otpAuthUri = useMemo(() => {
    if (!secretCode || !accountEmail) {
      return '';
    }
    return buildOtpAuthProvisioningUri(accountEmail, secretCode);
  }, [accountEmail, secretCode]);

  const showError = useCallback(
    (title: string) => {
      toast({
        duration: 3500,
        render: () => <CustomToast title={title} status='error' />,
      });
    },
    [toast],
  );

  const loadSetup = useCallback(async () => {
    const setupContext = readMfaSetupContext();
    if (!setupContext) {
      navigate('/', { replace: true });
      return;
    }
    setLoadingSecret(true);
    setLoadError(null);
    setAccountEmail(setupContext.email);
    try {
      const response = await apiService.mfa_setup_fetch(
        setupContext.setupToken,
        setupContext.cognitoAccessToken,
      );
      setSecretCode(response.data?.secret_code ?? null);
      if (!response.data?.secret_code) {
        setLoadError('Could not load setup information. Please sign in again.');
      }
    } catch (error: any) {
      setLoadError(
        mapAuthApiError(error, 'Could not load authenticator setup. Please sign in again.'),
      );
    } finally {
      setLoadingSecret(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadSetup();
  }, [loadSetup]);

  const goToSignIn = () => {
    clearMfaSessionStorage();
    navigate('/', { replace: true });
  };

  return (
    <AuthenticationMfaShell
      title='Set up two-factor authentication'
      description={
        <>
          Your organization requires you to set up{' '}
          <Box as='span' fontWeight='bold' fontFamily='regular_roboto' display='inline'>
            multi-factor authentication
          </Box>{' '}
          (MFA) before you can continue.
          <br />
          <br />
          Scan the QR code with your authenticator app (or enter the secret manually), then type the
          6-digit code to finish enrollment.
        </>
      }
    >
      {loadingSecret ? (
        <Spinner mt={4} color='green.900' />
      ) : loadError ? (
        <VStack spacing={4} align='stretch' mt={2}>
          <Text fontSize='sm' color='red.600' fontFamily='regular_roboto'>
            {loadError}
          </Text>
          <Button
            variant='outline'
            borderColor='green.900'
            color='green.900'
            onClick={goToSignIn}
            fontSize='sm'
          >
            Back to sign in
          </Button>
        </VStack>
      ) : (
        <>
          {otpAuthUri ? (
            <Box
              mt={3}
              p={3}
              borderRadius='xs'
              bg='white'
              borderWidth='1px'
              borderColor='gray.200'
              maxW='300px'
              display='flex'
              flexDirection='column'
              alignItems='center'
            >
              <Text
                fontSize='2xs'
                fontFamily='regular_roboto'
                color='#121826'
                mb={2}
                alignSelf='flex-start'
              >
                Scan with your authenticator app
              </Text>
              <Box p={2} bg='white' borderRadius='xs'>
                <QRCode
                  value={otpAuthUri}
                  size={200}
                  level='M'
                  title='Authenticator setup QR code'
                />
              </Box>
            </Box>
          ) : null}

          <Button
            mt={3}
            size='sm'
            variant='ghost'
            color='green.900'
            fontFamily='regular_roboto'
            onClick={() => {
              if (secretCode) {
                navigator.clipboard.writeText(secretCode).then(() => {
                  toast({
                    duration: 2000,
                    render: () => <CustomToast title='Secret copied' status='success' />,
                  });
                });
              }
            }}
          >
            Copy secret
          </Button>

          <Formik
            initialValues={initialValues}
            validationSchema={MfaTotpCodeSchema}
            onSubmit={async (form, { setSubmitting }) => {
              const setupContext = readMfaSetupContext();
              if (!setupContext) {
                goToSignIn();
                setSubmitting(false);
                return;
              }
              try {
                await apiService.mfa_verify_setup(
                  setupContext.setupToken,
                  setupContext.cognitoAccessToken,
                  form.code,
                );
                clearMfaSessionStorage();
                toast({
                  duration: 4000,
                  render: () => (
                    <CustomToast
                      title='Authenticator enrolled. Sign in again with your password.'
                      status='success'
                    />
                  ),
                });
                navigate('/', { replace: true });
              } catch (error: any) {
                showError(
                  mapAuthApiError(error, 'Verification failed. Check the code and try again.'),
                );
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ errors, touched, isSubmitting }) => (
              <Form>
                <VStack spacing='7' mb='1rem' mt={6}>
                  <FormField
                    label='Verification code'
                    name='code'
                    type='text'
                    labelStyles={{
                      fontSize: '14px',
                      color: '#121826',
                    }}
                    inputStyles={{
                      maxWidth: '300px',
                      boxShadow: '0px 0px 0px 1px #7A849E',
                    }}
                    placeholder='123456'
                    error={errors.code}
                    touched={touched.code}
                    disabled={isSubmitting}
                  />
                </VStack>
                <Button
                  type='submit'
                  w='100%'
                  maxWidth='300px'
                  h='12'
                  borderRadius='xs'
                  fontSize='sm'
                  bgColor='green.900'
                  disabled={isSubmitting}
                  _hover={{ bgColor: '' }}
                >
                  VERIFY AND ENROLL {isSubmitting && <Spinner ml={4} />}
                </Button>
              </Form>
            )}
          </Formik>

          <Heading
            mt='.6rem !important'
            color='black'
            display='flex'
            width='100%'
            fontFamily='regular_roboto'
          >
            <Text opacity={0.7} fontSize='2xs'>
              Wrong account?
            </Text>
            <Text
              ml={1}
              fontFamily='regular_roboto'
              fontSize='2xs'
              color='green.900'
              cursor='pointer'
              onClick={goToSignIn}
            >
              Back to sign in
            </Text>
          </Heading>
        </>
      )}
    </AuthenticationMfaShell>
  );
};

export default MfaSetupPage;
